import React, { ReactNode, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiHome, FiUsers, FiFilm, FiSettings, FiLogOut, FiMenu, FiX, FiBook, FiActivity } from 'react-icons/fi';
import logo from '../../assets/images/logos/logo.svg';

type MainLayoutProps = {
  children: ReactNode;
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const navLinks = [
    {
      path: '/home',
      label: 'Início',
      icon: <FiHome />,
      forAdmin: false
    },
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: <FiActivity />,
      forAdmin: true
    },
    {
      path: '/usuarios',
      label: 'Usuários',
      icon: <FiUsers />,
      forAdmin: true
    },
    {
      path: '/materiais',
      label: 'Materiais',
      icon: <FiFilm />,
      forAdmin: true
    },
    {
      path: '/bibliotecas',
      label: 'Bibliotecas',
      icon: <FiBook />,
      forAdmin: true
    },
    {
      path: '/perfil',
      label: 'Meu Perfil',
      icon: <FiSettings />,
      forAdmin: false
    }
  ];

  // Filtra os links com base no tipo do usuário
  const filteredLinks = navLinks.filter(link => 
    !link.forAdmin || (link.forAdmin && isAdmin)
  );

  return (
    <div className="min-h-screen bg-cinza-escuro flex flex-col font-montserrat">
      {/* Header */}
      <header className="bg-vermelho-escuro shadow-lg py-3 px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={toggleSidebar}
            className="md:hidden p-2 text-branco-acinzentado hover:text-white transition-colors"
            aria-label="Menu"
          >
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
          <Link to={isAdmin ? '/dashboard' : '/home'} className="ml-2">
            <img src={logo} alt="Logo" className="h-8" />
          </Link>
        </div>

        <div className="flex items-center space-x-3">
          {user && (
            <>
              <div className="hidden sm:block text-right">
                <div className="text-sm text-white font-medium">{user.nome}</div>
                <div className="text-xs text-branco-acinzentado opacity-80">{user.tipo_usuario === 'admin' ? 'Administrador' : 'Usuário'}</div>
              </div>
              <div className="h-9 w-9 rounded-full overflow-hidden bg-vermelho-claro border-2 border-white/20 flex items-center justify-center">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.nome} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-white text-sm font-bold">
                    {user.nome.substring(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar para mobile - aparece como um overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div 
              className="absolute inset-0 bg-black opacity-50" 
              onClick={closeSidebar}
              aria-hidden="true"
            ></div>
            <div className="relative flex flex-col w-64 max-w-xs h-full bg-gray-900 shadow-xl">
              <div className="py-5 px-4 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <span className="font-raleway text-xl font-bold text-vermelho-claro">Portal do Cliente</span>
                  <button 
                    onClick={closeSidebar}
                    className="text-gray-400 hover:text-white"
                    aria-label="Fechar menu"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>
              <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
                {filteredLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`
                      flex items-center px-4 py-3 text-sm font-medium rounded-md transition-all duration-200
                      ${location.pathname === link.path 
                        ? 'bg-vermelho-claro text-white shadow-md' 
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'}
                    `}
                    onClick={closeSidebar}
                  >
                    <span className="mr-3">{link.icon}</span>
                    {link.label}
                  </Link>
                ))}
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-800 hover:text-white transition-colors"
                >
                  <FiLogOut className="mr-3" />
                  Sair
                </button>
              </nav>
            </div>
          </div>
        )}

        {/* Sidebar para desktop - sempre visível */}
        <div className="hidden md:flex md:flex-shrink-0">
          <div className="flex flex-col w-64">
            <div className="flex flex-col h-0 flex-1 bg-gray-900">
              <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto scrollbar-thin">
                <div className="flex items-center flex-shrink-0 px-4 border-b border-gray-800 pb-4">
                  <span className="font-raleway text-xl font-bold text-vermelho-claro">Portal do Cliente</span>
                </div>
                <nav className="mt-5 flex-1 px-4 space-y-2">
                  {filteredLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`
                        group flex items-center px-4 py-3 text-sm font-medium rounded-md transition-all duration-200
                        ${location.pathname === link.path 
                          ? 'bg-vermelho-claro text-white shadow-md' 
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'}
                      `}
                    >
                      <span className={`mr-3 ${location.pathname === link.path ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>
                        {link.icon}
                      </span>
                      {link.label}
                    </Link>
                  ))}
                </nav>
              </div>
              <div className="flex-shrink-0 p-4 border-t border-gray-800">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-800 hover:text-white group transition-all duration-200"
                >
                  <FiLogOut className="mr-3 text-gray-400 group-hover:text-white" />
                  Sair
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo principal */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-cinza-escuro">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout; 